import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  InboxTabs,
  EmployeeTicket,
  InboxQueryDetails,
  InboxPageHookInterface,
  InboxFilterState,
} from "../utils/types";
import {
  useInboxQueryApiService,
  useInboxQueryListApiService,
} from "../service/api/InboxQueryApiService";
import { useToasts } from "../../../providers/notifications/ToastsContext";

/**
 * Enum for strapiId values used in toasts.
 */
enum ToastStrapiId {
  QUERY_INBOX_PERMISSION_ERROR = "query_inbox_permission_error",
  QUERY_INBOX_DOCUMENT_ERROR = "query_inbox_document_error",
  QUERY_INBOX_FILTER_ERROR = "query_inbox_filter_error",
}

/**
 * Custom hook to manage state and API interactions for the Inbox.
 *
 * @param {InboxTabs} activeTab - The current active tab.
 * @param {string} [searchQuery] - Optional search query string.
 * @param {InboxFilterState} [appliedFilters] - Optional filter state for filtering tickets.
 * @returns {InboxPageHookInterface} - State and functions for the inbox.
 */

const useInboxQueryHooks = (
  activeTab: InboxTabs,
  searchQuery?: string,
  appliedFilters?: InboxFilterState
): InboxPageHookInterface => {
  const { uuid, tab } = useParams();

  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>(1);
  const [openCount, setOpenCount] = useState<number | null>(null);
  const [resolvedCount, setResolvedCount] = useState<number | null>(null);
  const [currentCount, setCurrentCount] = useState<number | null>(null);
  const [tickets, setTickets] = useState<EmployeeTicket[]>([]);
  const [ticket, setTicket] = useState<InboxQueryDetails | null>(null);
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [pageLoaderVisible, setPageLoaderVisible] = useState<boolean>(true);

  const { showToast } = useToasts();
  const navigate = useNavigate();

  /**
   * Fetch the list of tickets based on the active tab and the page number
   * and set the tickets state
   */
  const { data, error, isLoading, isFetching } = useInboxQueryListApiService({
    page,
    activeTab,
    search: searchQuery,
    appliedFilters,
  });

  /**
   * Fetch the single ticket details based on the ticket ID and set the ticket state
   * if the ticket ID is present in the URL parameters and the single ticket data is available
   * otherwise set the ticket state to null and set the ticket ID to null
   */
  const {
    data: singleTicketData,
    error: singleTicketError,
    isLoading: singleTicketIsLoading,
    isFetching: singleTicketIsFetching,
  } = useInboxQueryApiService({ id: ticketId ?? "" });

  /**
   * Effect to set the tickets state based on the data received from the API
   */
  useEffect(() => {
    if (isFetching) {
      setTickets([]);
    } else if (data) {
      setPages(data.total_pages);
      setCurrentCount(data.count);

      // Update base counts when not searching
      if (!searchQuery) {
        if (activeTab === InboxTabs.OPEN) {
          setOpenCount(data.count);
        } else if (activeTab === InboxTabs.RESOLVED) {
          setResolvedCount(data.count);
        }
      }

      // If the data has results, set the tickets state to the results
      setTickets(data.results);
    } else {
      setTickets([]);
      setCurrentCount(0);
    }
  }, [data, activeTab, searchQuery, isFetching]);

  // Reset to first page when search query changes
  useEffect(() => {
    if (searchQuery) {
      setPage(1);
    }
  }, [searchQuery]);

  // Reset to first page when filters change
  useEffect(() => {
    if (appliedFilters) {
      setPage(1);
    }
  }, [appliedFilters]);

  /**
   * Effect to set the pageLoaderVisible state based on the loading and fetching states
   */
  useEffect(() => {
    if (
      isLoading ||
      isFetching ||
      singleTicketIsLoading ||
      singleTicketIsFetching
    ) {
      // If any of the loading or fetching states are true, show the loader
      setPageLoaderVisible(true);
    } else {
      setPageLoaderVisible(false);
    }
  }, [isLoading, isFetching, singleTicketIsLoading, singleTicketIsFetching]);

  /**
   *  Effect to set the ticket view state based on the ticket ID and single ticket data...
   *  if the ticket ID is present in the URL parameters and the single ticket data is available
   */
  useEffect(() => {
    if (ticketId && singleTicketData) {
      setTicket(singleTicketData);
    } else {
      setTicket(null);
    }
  }, [singleTicketData, ticketId]);

  useEffect(() => {
    if (uuid) {
      setTicketId(uuid);
    } else {
      setTicketId(null);
    }
  }, [uuid]);

  // Effect to handle general errors:
  // If there is an error fetching the list of tickets, show a toast and navigate to the home page.
  // Only navigate for serious errors (403, 500, etc.), not for empty results or minor issues
  useEffect(() => {
    if (!isFetching && !isLoading && error) {
      const status = error?.response?.status;

      if (status === 401) {
        // Handle session expiry/unauthorized access
        showToast({
          resourceType: "General",
          strapiId: "auth_session_expired",
        });
        navigate("/login");
      } else if (status === 400) {
        // Show specific error message for bad requests (filter validation errors)
        let errorMessage = "Invalid filter parameters";

        // Parse the specific error format: {"errors":["Invalid leave_types: Family"]}
        const apiErrors = error?.response?.data?.errors;
        if (Array.isArray(apiErrors) && apiErrors.length > 0) {
          errorMessage = apiErrors.join(", ");
        } else if (error?.response?.data?.message) {
          errorMessage = error?.response?.data?.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }

        showToast({
          strapiId: ToastStrapiId.QUERY_INBOX_FILTER_ERROR,
          data: {
            message: errorMessage,
          },
        });
      } else if (status === 403) {
        showToast({ strapiId: ToastStrapiId.QUERY_INBOX_PERMISSION_ERROR });
        navigate("/");
      } else if (status === 500 || status === 502 || status === 503) {
        // Server errors that indicate the service is down
        navigate("/");
      }
      // For other errors (like 404, etc.), don't navigate away
      // These might be due to filters resulting in no data or temporary issues
    }
  }, [error, isFetching, isLoading, navigate, showToast]);

  // Effect to handle single ticket errors:
  // If there is an error fetching the single ticket, show a toast and navigate to the query page.
  // Only navigate for permission errors, not for minor issues
  useEffect(() => {
    if (
      !singleTicketIsFetching &&
      !singleTicketIsLoading &&
      singleTicketError
    ) {
      const status = singleTicketError?.response?.status;

      if (status === 403) {
        showToast({ strapiId: ToastStrapiId.QUERY_INBOX_DOCUMENT_ERROR });
        navigate("/query");
      } else if (status === 404) {
        // Ticket not found, navigate back to list
        navigate("/query");
      }
      // For other errors, don't navigate away - might be temporary
    }
  }, [ticket, singleTicketError, navigate, showToast]);

  return {
    tab,
    uuid,
    page,
    pages,
    ticket,
    tickets,
    openCount,
    resolvedCount,
    currentCount,
    pageLoaderVisible,
    error: error || singleTicketError,
    setTicketId,
    setTicket,
    setPage,
  };
};

export default useInboxQueryHooks;
